import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { Globe2, ArrowLeft, Calendar, ShieldCheck } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_f59c783b-4061-4c90-8631-f537af31e122/artifacts/nm25t9qa_Calcutta%20Logo.png";

function fmtUSD(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n}`;
}

export function PublicGalleryList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/gallery");
        setRows(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="public-gallery-list">
      <header className="border-b border-slate-100 sticky top-0 bg-white/95 backdrop-blur z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO} alt="Calcutta" className="w-9 h-9 rounded-sm" />
            <span className="font-serif text-xl font-semibold tracking-tight">Calcutta</span>
          </Link>
          <Link to="/login" className="text-sm text-slate-700 hover:text-[#B33A3A] font-medium">Sign in</Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="max-w-2xl mb-12">
          <div className="eyebrow">Public IP Gallery</div>
          <h1 className="font-serif text-5xl md:text-6xl font-semibold tracking-tight mt-2">
            Inventions, in the open.
          </h1>
          <p className="text-slate-600 mt-4 text-base md:text-lg">
            A curated public-facing showcase of IP assets published by Calcutta members.
            Reach out to the owner directly for licensing or partnership.
          </p>
        </div>

        {loading && <div className="eyebrow text-slate-500">Loading gallery…</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rows.map((a) => (
            <Link
              key={a.id}
              to={`/p/${a.slug}`}
              data-testid={`gallery-card-${a.slug}`}
              className="bengal-card p-6 hover:shadow-md hover:border-slate-300 transition-all group"
            >
              <div className="flex items-center gap-2 mb-3 text-xs">
                <span className="eyebrow capitalize text-slate-500">{a.ip_type}</span>
                <span className="eyebrow text-slate-300">·</span>
                <span className="eyebrow font-mono text-slate-500 flex items-center gap-1">
                  <Globe2 size={10} /> {a.country}
                </span>
              </div>
              <h3 className="font-serif text-2xl font-medium tracking-tight line-clamp-2 min-h-[3.5rem]">{a.title}</h3>
              <p className="text-sm text-slate-600 mt-3 line-clamp-3 leading-relaxed">{a.abstract}</p>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-500">{a.category}</span>
                <span className="text-xs text-[#B33A3A] group-hover:underline font-medium">View →</span>
              </div>
            </Link>
          ))}
        </div>

        {!loading && rows.length === 0 && (
          <div className="text-center py-20 text-slate-500 text-sm">No public IP published yet.</div>
        )}
      </section>
    </div>
  );
}

export default function PublicGallery() {
  const { slug } = useParams();
  const [asset, setAsset] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/gallery/${slug}`);
        setAsset(data);
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-8">
        <div className="text-center">
          <div className="eyebrow">404</div>
          <h1 className="font-serif text-4xl font-semibold mt-2">Not public, or not found.</h1>
          <Link to="/gallery" className="text-[#B33A3A] mt-4 inline-block hover:underline">← Back to gallery</Link>
        </div>
      </div>
    );
  }
  if (!asset) return <div className="min-h-screen flex items-center justify-center eyebrow text-slate-500">Loading…</div>;

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen bg-white" data-testid="public-asset-page">
      <header className="border-b border-slate-100 sticky top-0 bg-white/95 backdrop-blur z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO} alt="Calcutta" className="w-9 h-9 rounded-sm" />
            <span className="font-serif text-xl font-semibold tracking-tight">Calcutta</span>
          </Link>
          <Link to="/gallery" className="text-sm text-slate-600 hover:text-[#B33A3A] flex items-center gap-1">
            <ArrowLeft size={14} /> Gallery
          </Link>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-center gap-2 mb-4">
          <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border bg-red-50 text-[#7A1F2B] border-red-200`}>
            {asset.ip_type}
          </span>
          <span className="eyebrow text-slate-500">{asset.category}</span>
          <span className="eyebrow text-slate-300">·</span>
          <span className="eyebrow font-mono text-slate-500 flex items-center gap-1">
            <Globe2 size={11} /> {asset.country}
          </span>
        </div>
        <h1 className="font-serif text-5xl md:text-6xl font-semibold tracking-tight leading-tight">{asset.title}</h1>
        <p className="text-slate-500 text-sm mt-3 flex items-center gap-4 flex-wrap">
          <span>by <strong className="text-slate-800">{asset.owner_name}</strong></span>
          {asset.filing_date && (
            <span className="flex items-center gap-1"><Calendar size={13} /> Filed {asset.filing_date}</span>
          )}
          {asset.filing_number && <span className="font-mono">{asset.filing_number}</span>}
        </p>

        <div className="mt-10 prose prose-slate max-w-none">
          <p className="text-lg leading-relaxed text-slate-700">{asset.abstract}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="bengal-card p-5">
            <div className="eyebrow">Status</div>
            <div className="font-serif text-2xl font-medium capitalize mt-1">{asset.status}</div>
          </div>
          <div className="bengal-card p-5">
            <div className="eyebrow">Valuation (AI est.)</div>
            <div className="font-mono text-2xl text-[#B33A3A] font-semibold mt-1">{fmtUSD(asset.valuation_usd)}</div>
          </div>
          <div className="bengal-card p-5">
            <div className="eyebrow">Renewal due</div>
            <div className="font-mono text-sm mt-1">{asset.renewal_due || "—"}</div>
          </div>
        </div>

        <div className="mt-10 bengal-card p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck size={20} className="text-[#B33A3A]" />
            <div>
              <div className="font-medium text-sm">Verified via Calcutta</div>
              <div className="text-xs text-slate-500">Ownership hash & filing history on file.</div>
            </div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard.writeText(shareUrl);
              alert("Link copied — share anywhere");
            }}
            className="text-sm bengal-btn-primary"
            data-testid="share-btn"
          >
            Copy shareable link
          </button>
        </div>
      </article>
    </div>
  );
}
